// TrainingDetailsPage.js
import React, { useEffect, useRef, useState } from 'react';
import './TrainingDetailsPage.scss';
import TitlePage from '../../components/TitlePage/TitlePage';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Video from './components/LessonVideo/LessonVideo';
import LessonText from './components/LessonText/LessonText';
import LessonAttachment from './components/LessonAttachment/LessonAttachment';
import LessonComment from './components/LessonComment/LessonComment';
import SvgIcons from '../../services/SvgIcons';
import LessonNav from './components/LessonNav/LessonNav';
import { updateVisibleChapters, handleLessonClick, navigateToPreviousLesson, navigateToNextLesson, createLessonKey, mapProgressDataToCompletedLessons, findFirstIncompleteLesson, getDefaultLessonToDisplay } from './utils/lessonUtils';
import { useMediaQuery } from 'react-responsive';
import LessonProgressButton from './components/LessonProgressButton/LessonProgressButton';
import { baseStrapiURL } from '../../services/config';
import Skeleton from '../../components/Skeleton/Skeleton';
import { strapiTrainingService } from '../../services/api/strapiTraining';
import Loading from '../../components/Loading/Loading';
import { useAuth } from '../../contexts/AuthContext';

const TrainingDetailsPage = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const isMobile = useMediaQuery({ maxWidth: 768 }); // Breakpoint pour MD
    const { trainingSlug } = useParams();
    const [trainingDetails, setTrainingDetails] = useState({});
    const [displayedMobileMenu, setDisplayedMobileMenu] = useState(false);
    const [selectedTrainingChapter, setSelectedTrainingChapter] = useState(null);
    const [selectedTrainingLesson, setSelectedTrainingLesson] = useState(null);
    const [visibleChapters, setVisibleChapters] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const contentRef = useRef(null);
    const titleRef = useRef(null);
    
    // Progression
    const [completedLessons, setCompletedLessons] = useState({});
    const [trainingProgress, setTrainingProgress] = useState(0);

    const location = useLocation();

    const displayMobileMenu = () => {
        setDisplayedMobileMenu(!displayedMobileMenu);
    };

    // Fonction pour mapper les données Strapi vers le format attendu par les composants
    const mapStrapiTrainingData = (strapiTraining) => {
        return {
            id: strapiTraining.id,
            documentId: strapiTraining.documentId,
            title: strapiTraining.Titre,
            slug: strapiTraining.slug,
            description: strapiTraining.Objectif,
            photo: strapiTraining.Photo,
            photoCard: strapiTraining.PhotoCarte,
            author: strapiTraining.Auteur,
            chapters: strapiTraining.Chapitres?.map(chapter => ({
                id: chapter.id,
                title: chapter.Titre,
                order_nb: chapter.Ordre,
                lessons: chapter.Lecons?.map(lesson => ({
                    id: lesson.id,
                    documentId: lesson.documentId,
                    title: lesson.Titre,
                    order_nb: lesson.Ordre,
                    text: lesson.Texte,
                    video: lesson.Video?.url || null,
                    attachments: lesson.PiecesJointes?.map(attachment => ({
                        id: attachment.id,
                        title: attachment.Titre,
                        file: attachment.Fichier?.url || null,
                        type: attachment.Fichier?.ext || 'unknown'
                    })) || []
                })).sort((a, b) => a.Ordre - b.Ordre) || []
            })).sort((a, b) => a.Ordre - b.Ordre) || [],
            // Inclure les données de progression
            userProgress: strapiTraining.userProgress
        };
    };

    const getTrainingDetails = async () => {
        try {
            setLoading(true);
            setError(null);

            // Étape 1: Récupérer la formation par slug pour obtenir l'ID
            const trainingBySlug = await strapiTrainingService.getTrainingBySlug(trainingSlug);
            
            if (!trainingBySlug) {
                throw new Error('Formation non trouvée');
            }
            
            // Étape 2: Accéder au contenu complet avec l'ID (incluant maintenant la progression)
            const trainingWithContent = await strapiTrainingService.accessTraining(trainingBySlug.data.id);

            console.log('*********** trainingWithContent ***********')
            console.log(trainingWithContent)
            
            // Mapper les données Strapi vers le format attendu
            const mappedTraining = mapStrapiTrainingData(trainingWithContent.data);
            setTrainingDetails(mappedTraining);

            // Traiter directement les données de progression incluses
            if (mappedTraining.userProgress) {
                console.log('Progression utilisateur trouvée:', mappedTraining.userProgress);
                
                // Mettre à jour la progression globale
                setTrainingProgress(mappedTraining.userProgress.progressPercentage || 0);
                
                // Mapper les leçons terminées
                const completedLessonsMap = mapProgressDataToCompletedLessons(
                    mappedTraining.userProgress.chapters || []
                );
                setCompletedLessons(completedLessonsMap);
                
                console.log('Progression mappée:', completedLessonsMap);

                // 🆕 NOUVEAU : Définir la leçon par défaut (première leçon non terminée)
                const defaultLesson = getDefaultLessonToDisplay(mappedTraining, completedLessonsMap);
                
                if (defaultLesson) {
                    console.log('🎯 Navigation automatique vers la première leçon non terminée:', {
                        chapter: defaultLesson.chapter.title,
                        lesson: defaultLesson.lesson.title,
                        chapterOrder: defaultLesson.chapter.order_nb,
                        lessonOrder: defaultLesson.lesson.order_nb
                    });
                    
                    setSelectedTrainingChapter(defaultLesson.chapter);
                    setSelectedTrainingLesson(defaultLesson.lesson);
                } else {
                    console.log('Toutes les leçons sont terminées, sélection de la première leçon');
                    // Fallback vers la première leçon si toutes sont terminées
                    const firstChapter = mappedTraining.chapters?.find(chapter => chapter.order_nb === 1);
                    if (firstChapter) {
                        setSelectedTrainingChapter(firstChapter);
                        const firstLesson = firstChapter.lessons?.find(lesson => lesson.order_nb === 1);
                        if (firstLesson) {
                            setSelectedTrainingLesson(firstLesson);
                        }
                    }
                }
            } else {
                // Pas de données de progression : utiliser la première leçon (logique existante)
                console.log('Pas de données de progression, sélection de la première leçon');
                const firstChapter = mappedTraining.chapters?.find(chapter => chapter.order_nb === 1);
                if (firstChapter) {
                    setSelectedTrainingChapter(firstChapter);
                    const firstLesson = firstChapter.lessons?.find(lesson => lesson.order_nb === 1);
                    if (firstLesson) {
                        setSelectedTrainingLesson(firstLesson);
                    }
                }
            }

        } catch (error) {
            console.error('Erreur lors de la récupération de la formation:', error);
            setError(error.message);
            
            // Gérer les erreurs d'accès
            if (error.message.includes('Accès non autorisé') || error.message.includes('non connecté')) {
                navigate('/connexion');
            }
        } finally {
            setLoading(false);
        }
    };

    // Fonction pour générer l'URL des médias Strapi
    const lessonMediaUrl = (mediaPath) => {
        if (!mediaPath) return null;
        return mediaPath.startsWith('http') ? mediaPath : `${baseStrapiURL}${mediaPath}`;
    };

    useEffect(() => {
        window.scroll(0, 0);
    }, []);

    useEffect(() => {
        if (trainingSlug) {
            getTrainingDetails();
        }
    }, [trainingSlug]);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/connexion');
        }
    }, [isAuthenticated, navigate]);

    if (loading) {
        return <Loading />
    }

    if (error) {
        return (
            <div className="training-details-page">
                <Helmet>
                    <title>Erreur | Formation</title>
                </Helmet>
                <div className="training-details-page__nav_content_container">
                    <div className="training-details-page__nav_content">
                        <div style={{ padding: '2rem', textAlign: 'center' }}>
                            <h1>Erreur</h1>
                            <p>{error}</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Debug : vérifier l'état de la leçon sélectionnée
    console.log('selectedTrainingChapter:', selectedTrainingChapter);
    console.log('selectedTrainingLesson:', selectedTrainingLesson);
    console.log('completedLessons:', completedLessons);
    
    // Calculer la clé de leçon pour le debug
    const currentLessonKey = selectedTrainingChapter && selectedTrainingLesson ? 
        createLessonKey(selectedTrainingChapter, selectedTrainingLesson) : null;
    console.log('currentLessonKey:', currentLessonKey);
    console.log('isLessonCompleted:', currentLessonKey ? completedLessons[currentLessonKey] : false);

    return (
        <div className="training-details-page">
            <Helmet>
                <title>{(trainingDetails.title ? trainingDetails.title : 'Ma formation') + ' | Soutien émotionnel des mamans | Aide à la parentalité | Montpellier'}</title>
            </Helmet>
            
            <TitlePage
                title1={trainingDetails.title || 'Ma formation'}
                font1="handwrite-title"
                title2=""
                font2="classic-title"
                picture={trainingDetails.photo}
            />
            
            <div className="training-details-page__nav_content_container">
                <div
                    ref={contentRef}
                    className="training-details-page__nav_content"
                >
                    <LessonNav
                        trainingDetails={trainingDetails}
                        displayedMobileMenu={displayedMobileMenu}
                        setDisplayedMobileMenu={setDisplayedMobileMenu}
                        setSelectedTrainingLesson={setSelectedTrainingLesson}
                        setSelectedTrainingChapter={setSelectedTrainingChapter}
                        selectedTrainingChapter={selectedTrainingChapter}
                        selectedTrainingLesson={selectedTrainingLesson}
                        contentRef={contentRef}
                        titleRef={titleRef}
                        visibleChapters={visibleChapters}
                        setVisibleChapters={setVisibleChapters}
                        isMobile={isMobile}
                        trainingProgress={trainingProgress}
                        setTrainingProgress={setTrainingProgress}
                        completedLessons={completedLessons}
                        setCompletedLessons={setCompletedLessons}
                        setError={setError}
                    />
                    
                    <div className="training-details-page__content">
                        <div className='training-details-page__content_nav_btn_container_mobile'>
                            <div
                                className={
                                    `training-details-page__content_nav_btn_mobile 
                                    ${selectedTrainingChapter && selectedTrainingChapter.order_nb === 1 && selectedTrainingLesson && selectedTrainingLesson.order_nb === 1 ?
                                        'opacity'
                                        : ''
                                    }`
                                }
                                onClick={
                                    () => navigateToPreviousLesson(
                                        selectedTrainingChapter,
                                        selectedTrainingLesson,
                                        trainingDetails,
                                        (chapter, lesson) => handleLessonClick(
                                            trainingDetails,
                                            chapter,
                                            lesson,
                                            setSelectedTrainingChapter,
                                            setSelectedTrainingLesson,
                                            updateVisibleChapters,
                                            setVisibleChapters,
                                            setDisplayedMobileMenu,
                                            contentRef,
                                            titleRef,
                                            isMobile
                                        )
                                    )
                                }
                            >
                                <SvgIcons
                                    name={'chevron_back'}
                                    color='#E6CAA3'
                                    width={32}
                                    height={32}
                                    backgroundColor='#361215'
                                    borderRadius={50}
                                    padding={'10px'}
                                />
                            </div>
                            <div
                                ref={titleRef}
                                className='training-details-page__chapter_lesson_container'
                            >
                                <div className='training-details-page__chapter'>
                                    {
                                        selectedTrainingChapter ?
                                            <span>{selectedTrainingChapter.title}</span>
                                        :
                                            <Skeleton
                                                width='100%'
                                                height='40px'
                                                borderRadius='5px'
                                                bkgColor='#EEE6DB'
                                                animColor='#F5F0ED'
                                                margin='0'
                                                opacity={0.6}
                                            />
                                    }
                                </div>
                                <div className='training-details-page__lesson'>
                                    {
                                        selectedTrainingLesson ?
                                            <span>{selectedTrainingLesson.title}</span>
                                        :
                                            <Skeleton
                                                width='100%'
                                                height='40px'
                                                borderRadius='5px'
                                                bkgColor='#EEE6DB'
                                                animColor='#F5F0ED'
                                                margin='0'
                                                opacity={0.6}
                                            />
                                    }
                                </div>
                            </div>
                            <div
                                className={
                                    `training-details-page__content_nav_btn_mobile 
                                    ${selectedTrainingChapter && 
                                      selectedTrainingChapter.order_nb === trainingDetails.chapters?.length && 
                                      selectedTrainingLesson && 
                                      selectedTrainingLesson.order_nb === selectedTrainingChapter.lessons?.length ?
                                        'opacity'
                                        : ''
                                    }`
                                }
                                onClick={
                                    () => navigateToNextLesson(
                                        selectedTrainingChapter,
                                        selectedTrainingLesson,
                                        trainingDetails,
                                        (chapter, lesson) => handleLessonClick(
                                            trainingDetails,
                                            chapter,
                                            lesson,
                                            setSelectedTrainingChapter,
                                            setSelectedTrainingLesson,
                                            updateVisibleChapters,
                                            setVisibleChapters,
                                            setDisplayedMobileMenu,
                                            contentRef,
                                            titleRef,
                                            isMobile
                                        )
                                    )
                                }
                            >
                                <SvgIcons
                                    name={'chevron_back'}
                                    color='#E6CAA3'
                                    width={32}
                                    height={32}
                                    backgroundColor='#361215'
                                    borderRadius={50}
                                    padding={'10px'}
                                    transform={'rotate(180deg)'}
                                />
                            </div>
                        </div>
                        
                        <Video
                            selectedTrainingLesson={selectedTrainingLesson}
                            lessonMediaUrl={lessonMediaUrl}
                        />
                        
                        <div className="lesson-progress-button">
                            {
                                selectedTrainingLesson && selectedTrainingChapter && trainingDetails.id ?
                                    <LessonProgressButton
                                        trainingId={trainingDetails.id}
                                        selectedTrainingChapter={selectedTrainingChapter}
                                        selectedTrainingLesson={selectedTrainingLesson}
                                        trainingDetails={trainingDetails}
                                        setSelectedTrainingChapter={setSelectedTrainingChapter}
                                        setSelectedTrainingLesson={setSelectedTrainingLesson}
                                        setCompletedLessons={setCompletedLessons}
                                        setTrainingProgress={setTrainingProgress}
                                        updateVisibleChapters={updateVisibleChapters}
                                        setVisibleChapters={setVisibleChapters}
                                        setDisplayedMobileMenu={setDisplayedMobileMenu}
                                        contentRef={contentRef}
                                        titleRef={titleRef}
                                        isMobile={isMobile}
                                        isLessonCompleted={
                                            currentLessonKey ? completedLessons[currentLessonKey] || false : false
                                        }
                                        completedLessons={completedLessons}
                                    />
                                    :
                                    <Skeleton
                                        width='143px'
                                        height='47px'
                                        borderRadius='5px'
                                        bkgColor='#EEE6DB'
                                        animColor='#F5F0ED'
                                        margin='0'
                                    />
                            }
                        </div>
                        
                        <LessonText
                            selectedTrainingLesson={selectedTrainingLesson}
                        />
                        
                        <LessonAttachment
                            selectedTrainingLesson={selectedTrainingLesson}
                            lessonMediaUrl={lessonMediaUrl}
                        />
                        
                        {/* <LessonComment
                            selectedTrainingLesson={selectedTrainingLesson}
                        /> */}
                    </div>
                </div>
                
                <div className="training-details-page__menu-btn-mobile-container"
                    onClick={() => displayMobileMenu()}
                >
                    <SvgIcons
                        name={displayedMobileMenu ? 'close' : 'list'}
                        color='#E6CAA3'
                        width={48}
                        height={48}
                        backgroundColor='#361215'
                        borderRadius={50}
                        padding={'10px'}
                    />
                </div>
            </div>
        </div>
    );
};

export default TrainingDetailsPage;